import { motion } from "framer-motion";
import { Bot, BrainCircuit, Workflow, LineChart, LayoutDashboard, Terminal } from "lucide-react";

const features = [
  { name: "AI Agents", desc: "Autonomous task runners.", icon: Bot, color: "from-neon-cyan to-neon-blue" },
  { name: "AI Assistants", desc: "Copilots embedded across tools.", icon: BrainCircuit, color: "from-neon-purple to-neon-magenta" },
  { name: "Automation Workflows", desc: "Multi-step business pipelines.", icon: Workflow, color: "from-neon-orange to-neon-magenta" },
  { name: "Predictive Systems", desc: "Forecast revenue, churn & demand.", icon: LineChart, color: "from-emerald-400 to-neon-cyan" },
  { name: "Custom Dashboards", desc: "One-pane-of-glass for the team.", icon: LayoutDashboard, color: "from-neon-blue to-neon-purple" },
];

const log = [
  { t: "12:04:21", c: "neon-cyan", m: "agent.lead-router started" },
  { t: "12:04:22", c: "neon-magenta", m: "→ enrich(\"acme.io\") • 84 fields" },
  { t: "12:04:23", c: "neon-cyan", m: "score=A+ • route → AE.priya" },
  { t: "12:04:24", c: "emerald-400", m: "✓ Slack notified • CRM updated" },
  { t: "12:04:26", c: "neon-cyan", m: "agent.followup queued · 24h" },
  { t: "12:04:28", c: "neon-orange", m: "metric.ARR += ₹42,000" },
];

export default function AiLab() {
  return (
    <section id="ai-lab" data-testid="ai-lab-section" className="section relative overflow-hidden">
      {/* Background orb */}
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] h-[80vw] max-w-[1100px] max-h-[1100px] rounded-full opacity-30 blur-3xl bg-[conic-gradient(from_0deg,#00F0FF,#8A2BE2,#FF007F,#FF5E00,#00F0FF)] spin-slow" />
      </div>

      <div className="container-x">
        <div className="max-w-3xl mb-14">
          <p className="overline mb-4">AI & Automation Lab</p>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">
            The growth ops team you can&apos;t <span className="text-gradient-neon">hire</span>.
          </h2>
          <p className="mt-5 text-white/60 max-w-xl">
            Our in-house lab designs and ships AI systems that do the
            unglamorous work — 24/7, with the precision of a senior operator.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* Features grid */}
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-10%" }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="glass rounded-2xl p-6 relative overflow-hidden group hover:border-white/20"
                  data-testid={`ai-feature-${i}`}
                >
                  <div className={`absolute -top-24 -right-24 w-56 h-56 rounded-full blur-3xl opacity-25 bg-gradient-to-tr ${f.color}`} />
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${f.color} flex items-center justify-center`}>
                    <Icon size={18} />
                  </div>
                  <h3 className="mt-5 text-lg font-bold tracking-tight">{f.name}</h3>
                  <p className="mt-1.5 text-sm text-white/55">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>

          {/* Terminal */}
          <div className="lg:col-span-5">
            <div className="glass rounded-2xl overflow-hidden h-full">
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-black/30">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
                </div>
                <div className="flex items-center gap-2 text-[11px] font-mono text-white/50">
                  <Terminal size={12} />
                  novarise.agents.live
                </div>
                <span className="text-[11px] font-mono text-emerald-400">● running</span>
              </div>
              <div className="p-5 font-mono text-[12.5px] leading-7">
                {log.map((l, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35, delay: i * 0.25 }}
                    className="flex gap-3"
                  >
                    <span className="text-white/30">{l.t}</span>
                    <span className={`text-${l.c}`} style={{ color: cssColor(l.c) }}>
                      {l.m}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function cssColor(token) {
  const map = {
    "neon-cyan": "#00F0FF",
    "neon-magenta": "#FF007F",
    "neon-orange": "#FF5E00",
    "neon-purple": "#8A2BE2",
    "neon-blue": "#007AFF",
    "emerald-400": "#34d399",
  };
  return map[token] || "#fff";
}
